import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormError } from '@/components/ui/form-error';
import { customColorSchema, type CustomColorFormData } from '@/lib/validations';
import { COLOR_FAMILIES } from '@/data/sherwin-williams-colors';
import { Loader2, Palette } from 'lucide-react';

interface AddCustomColorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (color: CustomColorFormData) => Promise<void>;
  isSubmitting?: boolean;
  error?: string;
}

export function AddCustomColorDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  error,
}: AddCustomColorDialogProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CustomColorFormData>({
    resolver: zodResolver(customColorSchema),
    defaultValues: {
      code: '',
      name: '',
      hex: '',
      family: undefined,
      lrv: undefined,
    },
  });

  const hexValue = watch('hex');

  const handleFormSubmit = async (data: CustomColorFormData) => {
    await onSubmit(data);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Add Custom Color
          </DialogTitle>
          <DialogDescription>
            Add a custom Sherwin-Williams color to your palette.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 mt-4">
          {/* Color Code */}
          <div className="space-y-2">
            <Label htmlFor="code">
              Color Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              placeholder="SW-7029"
              {...register('code')}
              disabled={isSubmitting}
              className={errors.code ? 'border-destructive' : ''}
            />
            <FormError message={errors.code?.message} />
            <p className="text-xs text-muted-foreground">
              Format: SW-XXXX (e.g., SW-7029)
            </p>
          </div>

          {/* Color Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Color Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Agreeable Gray"
              {...register('name')}
              disabled={isSubmitting}
              className={errors.name ? 'border-destructive' : ''}
            />
            <FormError message={errors.name?.message} />
          </div>

          {/* Hex Color with Preview */}
          <div className="space-y-2">
            <Label htmlFor="hex">
              Hex Color <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="hex"
                  placeholder="#D7D2CB"
                  {...register('hex')}
                  disabled={isSubmitting}
                  className={errors.hex ? 'border-destructive' : ''}
                />
              </div>
              <div
                className="w-12 h-10 rounded-md border border-input shrink-0"
                style={{
                  backgroundColor:
                    hexValue && /^#?[0-9A-Fa-f]{6}$/.test(hexValue.replace('#', ''))
                      ? hexValue.startsWith('#')
                        ? hexValue
                        : `#${hexValue}`
                      : '#ffffff',
                }}
                aria-label="Color preview"
              />
            </div>
            <FormError message={errors.hex?.message} />
            <p className="text-xs text-muted-foreground">
              Format: #RRGGBB (e.g., #D7D2CB)
            </p>
          </div>

          {/* Color Family */}
          <div className="space-y-2">
            <Label htmlFor="family">
              Color Family <span className="text-destructive">*</span>
            </Label>
            <Controller
              name="family"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isSubmitting}
                >
                  <SelectTrigger
                    id="family"
                    className={errors.family ? 'border-destructive' : ''}
                  >
                    <SelectValue placeholder="Select a family" />
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_FAMILIES.map((family) => (
                      <SelectItem key={family} value={family}>
                        {family}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FormError message={errors.family?.message} />
          </div>

          {/* LRV (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="lrv">Light Reflectance Value (LRV)</Label>
            <Input
              id="lrv"
              type="number"
              step="0.01"
              min="0"
              max="100"
              placeholder="63.5"
              {...register('lrv', { valueAsNumber: true })}
              disabled={isSubmitting}
              className={errors.lrv ? 'border-destructive' : ''}
            />
            <FormError message={errors.lrv?.message} />
            <p className="text-xs text-muted-foreground">
              Optional. Range: 0-100 (higher = lighter)
            </p>
          </div>

          {/* General Error Display */}
          {error && (
            <div className="rounded-md bg-destructive/10 p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Palette className="mr-2 h-4 w-4" />
                  Add Color
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
